import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CONSTANTS } from '../constants/constants';
import { iif, map, Observable, tap } from 'rxjs';
import { Category } from '../types/event-response.type';
import { unwrapCategoriesUtil } from '../utils/unwrapCategories.util';

@Injectable({
  providedIn: 'root',
})
export class CategoriesHttpService {
  constructor(private http: HttpClient) {}

  private categories: Category[];

  getCategories(
    params: {
      category_title?: string;
    } = {}
  ): Observable<Category[]> {
    if (this.categories && params?.category_title?.length === 0) {
      return new Observable((subscriber) => {
        subscriber.next(this.categories);
      });
    }

    return this.http
      .get<Category[]>(`${CONSTANTS.BASE_API}categories`, {
        params: new HttpParams({ fromObject: params }),
      })
      .pipe(
        tap((categories) => {
          this.categories = categories;
        })
      );
  }

  getCategoriesWithEvents(): Observable<Category[]> {
    return this.getCategories().pipe(
      map((items) => items.filter((item) => item.event_count > 0))
    );
  }

  getUnwrappedCategoriesSortedByNumberOfEvents(
    sliceEnd?: number
  ): Observable<Category[]> {
    return this.getUnwrappedCategories().pipe(
      map((items) =>
        items
          .filter((item) => item.event_count > 0)
          .sort((a, b) => b.event_count - a.event_count)
          .slice(0, sliceEnd)
      )
    );
  }

  getUnwrappedCategories(
    params: {
      category_title?: string;
    } = {}
  ): Observable<Category[]> {
    return this.getCategories(params).pipe(
      map((res) => unwrapCategoriesUtil(res))
    );
  }

  saveUpdateCategory(category: Category): Observable<Object> {
    return iif(
      () => !!category.category_id,
      this.updateCategory(category, category.category_id),
      this.saveCategory(category)
    );
  }

  saveCategory(body: any): Observable<Object> {
    return this.http.post(`${CONSTANTS.BASE_API}categories`, body);
  }

  updateCategory(body: any, id: number): Observable<Object> {
    return this.http.patch(`${CONSTANTS.BASE_API}categories/${id}`, body);
  }

  deleteCategory(id: number): Observable<Object> {
    return this.http.delete(`${CONSTANTS.BASE_API}categories/${id}`);
  }

  followCategory(id: number): Observable<Object> {
    return this.http.get(`${CONSTANTS.BASE_API}categories/${id}/follow`);
  }
}
